/// <reference lib="dom" />
//
// Sealing-element calculator island (mobile-style controls, full engineering view).
//
// Slider-first: the installer drags the inner (pipe) and outer (bore / core-hole)
// diameters. A live cross-section shows the bore, pipe, annular gap and the chain
// links laid out as ring segments — making the sizing algorithm visible. Results
// render live: matching catalogue products (real DB Dichtungskette variants,
// matched by bore Ø ≈ DK size), the matching-sizes engineering table, and
// Call / Mail to order. See ../shared/sealChains.ts + ../server/sealingCatalog.ts.

export {}; // module scope

type SealChain = {
  id: string; size: string; material: string;
  adRohrMin: number; adRohrMax: number;
  gapMin: number; gapMax: number; linkWidthMm: number;
};
type SealProduct = {
  variantId: number; sku: string; name: string; material: string; size: number;
  priceText: string; priceFallback: boolean; imageUrl: string | null; href: string;
};
type SealCalcData = {
  chains: SealChain[];
  products: SealProduct[];
  materials: { key: string; label: string }[];
  contact: { phone: string; email: string };
  lang: string;
  strings: Record<string, string>;
};

const DEFAULT_IMAGE =
  "https://www.ibeco.se/media/1s4osjtc/il_blue.jpg?rmode=pad&ranchor=center&width=600&height=600&quality=90&upscale=true&bgcolor=ffffff";

const MATERIAL_COLORS: Record<string, string> = {
  epdm: "#2563eb", nitril: "#6b8e23", ktw: "#0891b2", silikon: "#be123c",
};

const el = {
  data: document.getElementById("seal-data"),
  inner: document.getElementById("seal-inner") as HTMLInputElement | null,
  outer: document.getElementById("seal-outer") as HTMLInputElement | null,
  innerValue: document.getElementById("seal-inner-value"),
  outerValue: document.getElementById("seal-outer-value"),
  summary: document.getElementById("seal-summary"),
  status: document.getElementById("seal-status"),
  viz: document.getElementById("seal-viz"),
  readout: document.getElementById("seal-readout"),
  results: document.getElementById("seal-results"),
};

type Els = {
  data: HTMLElement;
  inner: HTMLInputElement; outer: HTMLInputElement;
  innerValue: HTMLElement; outerValue: HTMLElement;
  summary: HTMLElement; status: HTMLElement; viz: HTMLElement;
  readout: HTMLElement; results: HTMLElement;
};

if (
  el.data && el.inner && el.outer && el.innerValue && el.outerValue &&
  el.summary && el.status && el.viz && el.readout && el.results
) {
  boot(el as unknown as Els);
}

function boot(el: Els) {
  const data = parseData(el.data);
  const s = data.strings;
  const materialLabel = new Map(data.materials.map((m) => [m.key, m.label]));
  const nf = new Intl.NumberFormat(data.lang === "de" ? "de-DE" : "en-US", { maximumFractionDigits: 1 });
  const mm = (n: number) => `${nf.format(n)} mm`;
  const matColor = (m: string) => MATERIAL_COLORS[m] ?? "#2563eb";
  const linkCount = (pipe: number, w: number) => Math.max(2, Math.round((Math.PI * pipe) / w));

  el.inner.addEventListener("input", update);
  el.outer.addEventListener("input", update);
  // Delegated: add-to-basket forms are re-injected on every change, so bind once
  // on the stable results container.
  el.results.addEventListener("submit", onAddToBasket);
  update();

  function inner() { return Number(el.inner.value); }
  function outer() { return Number(el.outer.value); }

  function linksFor(pipe: number, material: string): number {
    const chain =
      data.chains.find((c) => c.material === material && pipe >= c.adRohrMin && pipe <= c.adRohrMax) ??
      data.chains.find((c) => pipe >= c.adRohrMin && pipe <= c.adRohrMax) ??
      nearest(pipe);
    return linkCount(pipe, chain?.linkWidthMm ?? 35);
  }

  function onAddToBasket(e: Event) {
    const form = (e.target as HTMLElement).closest<HTMLFormElement>("form.seal-add");
    if (!form) return;
    e.preventDefault();
    const variantId = form.dataset.variantId;
    if (!variantId) return;
    const qtyInput = form.querySelector<HTMLInputElement>('input[name="qty"]');
    const qty = Math.max(1, Math.floor(Number(qtyInput?.value || 1)));
    const btn = el.results.querySelector<HTMLButtonElement>(`button[form="${form.id}"]`);
    if (btn) btn.disabled = true;
    fetch("/cart/add", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded", Accept: "application/json" },
      body: new URLSearchParams({ variantId, qty: String(qty) }),
    })
      .then((r) => {
        if (!r.ok) throw new Error("add failed");
        bumpCount(qty);
        if (btn) flashAdded(btn, s.prod_added ?? "Added");
      })
      .catch(() => {
        if (btn) btn.disabled = false;
      });
  }

  function update() {
    const pipe = inner();
    const bore = outer();
    el.innerValue.textContent = `${pipe} mm`;
    el.outerValue.textContent = `${bore} mm`;

    if (!(bore > pipe)) {
      el.summary.innerHTML = `<p class="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800"><i class="fa-solid fa-triangle-exclamation mr-1"></i>${esc(
        s.bore_invalid,
      )}</p>`;
      el.status.innerHTML = "";
      el.viz.innerHTML = placeholderSvg();
      el.readout.innerHTML = "";
      el.results.innerHTML = "";
      return;
    }

    const gap = (bore - pipe) / 2;
    // Recommended chain: among sizes whose pipe-Ø range covers this pipe, prefer
    // one whose annular gap also fits, then EPDM (general purpose). The matching-
    // sizes table below lists every fitting option with its coverage ranges, so
    // the installer can pick an alternative that also covers nearby pipe Ø.
    const active = pickActive(pipe, bore);
    const links = active ? linkCount(pipe, active.linkWidthMm) : linkCount(pipe, 35);
    const segColor = matColor(active?.material ?? "epdm");

    el.summary.innerHTML = `<div class="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2 text-sm">
        <span class="text-slate-500">${esc(s.seal_thickness)}</span>
        <span class="font-semibold tabular-nums text-slate-900">${mm(gap)}</span>
      </div>`;
    el.status.innerHTML = "";
    el.viz.innerHTML = buildSvg(pipe, bore, links, segColor);
    el.readout.innerHTML = renderReadout(pipe, bore, gap, links, active, segColor);

    el.results.innerHTML = sizesBlock(pipe, bore, active) + productsBlock(pipe, bore) + orderBlock();
  }

  // ---- recommendation + matching-sizes coverage table -----------------------

  // Pick the best chain to recommend for this pipe/bore combination.
  function pickActive(pipe: number, bore: number): SealChain | undefined {
    const byPipe = data.chains.filter((c) => pipe >= c.adRohrMin && pipe <= c.adRohrMax);
    if (!byPipe.length) return nearest(pipe);
    const gap = (bore - pipe) / 2;
    const gapFits = byPipe.filter((c) => gap >= c.gapMin && gap <= c.gapMax);
    const pool = gapFits.length ? gapFits : byPipe;
    return pool.find((c) => c.material === "epdm") ?? pool[0];
  }

  // Function declaration (not a const) so the initial update() at boot — which
  // runs before this point in the body — can already call it without a TDZ error.
  function rangeMm(a: number, b: number): string {
    return `${nf.format(a)}–${nf.format(b)} mm`;
  }

  // The matching-sizes engineering table: every size that covers this pipe Ø,
  // each with the pipe-Ø range it covers (so a 74 mm input also reveals which
  // size still covers 72 / 75 mm) and the required bore-Ø range. When nothing
  // fits, the nearest sizes are shown instead.
  function sizesBlock(pipe: number, bore: number, active: SealChain | undefined): string {
    const gap = (bore - pipe) / 2;
    const fitting = data.chains.filter((c) => pipe >= c.adRohrMin && pipe <= c.adRohrMax);
    const usingSuggest = fitting.length === 0;
    const list = usingSuggest ? sortByNearest(data.chains, pipe).slice(0, 4) : fitting;
    // Gap-fitting sizes first, then by size class, then material.
    const ordered = [...list].sort((a, b) => {
      const af = gap >= a.gapMin && gap <= a.gapMax ? 0 : 1;
      const bf = gap >= b.gapMin && gap <= b.gapMax ? 0 : 1;
      return af - bf || a.size.localeCompare(b.size) || a.material.localeCompare(b.material);
    });

    const rows = ordered
      .map((c) => {
        const isRec = !!active && c.id === active.id;
        const gapFits = gap >= c.gapMin && gap <= c.gapMax;
        const links = linkCount(pipe, c.linkWidthMm);
        const rowCls = isRec ? "bg-brand-50" : gapFits ? "" : "text-slate-400";
        const star = isRec
          ? ` <span class="ml-1 rounded bg-brand-600 px-1.5 py-0.5 text-[10px] font-semibold text-white"><i class="fa-solid fa-star"></i></span>`
          : "";
        return `<tr class="${rowCls}">
          <td class="font-medium text-slate-900">${esc(s.size_prefix)} ${esc(c.size)}${star}</td>
          <td><span class="inline-flex items-center gap-1.5"><span class="inline-block h-2.5 w-2.5 rounded-sm" style="background:${matColor(
            c.material,
          )}"></span>${esc(materialLabel.get(c.material) ?? c.material)}</span></td>
          <td class="tabular-nums">${rangeMm(c.adRohrMin, c.adRohrMax)}</td>
          <td class="tabular-nums">${rangeMm(c.gapMin, c.gapMax)}</td>
          <td class="tabular-nums">${rangeMm(pipe + 2 * c.gapMin, pipe + 2 * c.gapMax)}</td>
          <td class="tabular-nums font-semibold text-brand-700">${links} <span class="font-normal text-slate-400">${esc(
            s.links_suffix,
          )}</span></td>
        </tr>`;
      })
      .join("");

    const header = usingSuggest
      ? `<h2 class="mt-0">${esc(s.none_title)}</h2><p class="-mt-1 mb-2 text-sm text-slate-500">${esc(s.none_hint)}</p>`
      : `<h2 class="mt-0">${esc(s.results_title)}</h2>`;

    return `${header}
      <div class="card mb-6 overflow-x-auto">
        <table class="table">
          <thead><tr>
            <th>${esc(s.col_size)}</th>
            <th>${esc(s.col_material)}</th>
            <th>${esc(s.col_pipe)}</th>
            <th>${esc(s.col_gap)}</th>
            <th>${esc(s.col_bore)}</th>
            <th>${esc(s.col_links)}</th>
          </tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </div>`;
  }

  // ---- catalogue product cards (matched by bore Ø ≈ DK size) -----------------

  function matchProducts(bore: number): SealProduct[] {
    const covering = new Map<string, SealProduct>();
    const largest = new Map<string, SealProduct>();
    for (const p of data.products) {
      // data.products is sorted by size ascending (server-side).
      const big = largest.get(p.material);
      if (!big || p.size > big.size) largest.set(p.material, p);
      if (p.size >= bore && !covering.has(p.material)) covering.set(p.material, p);
    }
    const out: SealProduct[] = [];
    for (const m of new Set(data.products.map((p) => p.material))) {
      const pick = covering.get(m) ?? largest.get(m);
      if (pick) out.push(pick);
    }
    return out.sort((a, b) => a.size - b.size || a.material.localeCompare(b.material));
  }

  function productsBlock(pipe: number, bore: number): string {
    const products = matchProducts(bore);
    if (!products.length) {
      return `<h2 class="mt-0">${esc(s.products_title)}</h2>
        <div class="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <i class="fa-solid fa-circle-exclamation mr-1"></i>${esc(s.no_match)}
        </div>`;
    }
    const body = products
      .map((p, i) => {
        const img = p.imageUrl
          ? `<img src="${esc(p.imageUrl)}" alt="${esc(p.sku)}" loading="lazy" class="h-9 w-9 rounded border border-slate-200 object-contain"/>`
          : `<img src="${DEFAULT_IMAGE}" alt="${esc(p.sku)}" loading="lazy" class="h-9 w-9 rounded border border-slate-200 object-cover"/>`;
        const price = `${esc(p.priceText)}${p.priceFallback ? ` <span class="text-[10px] text-slate-400">(${esc(s.price_fallback)})</span>` : ""}`;
        const links = linksFor(pipe, p.material);
        const formId = `seal-add-${i}`;
        return `<tr>
          <td>
            <a href="${esc(p.href)}" class="flex items-center gap-2.5 font-medium text-slate-900 hover:text-brand-700">
              ${img}
              <span class="min-w-0">
                <span class="block truncate">${esc(p.name)}</span>
                <span class="block text-xs font-normal text-slate-400">${esc(p.sku)}</span>
              </span>
            </a>
          </td>
          <td class="tabular-nums">${mm(p.size)}</td>
          <td><span class="inline-flex items-center gap-1.5"><span class="inline-block h-2.5 w-2.5 rounded-sm" style="background:${matColor(
            p.material,
          )}"></span>${esc(materialLabel.get(p.material) ?? p.material)}</span></td>
          <td class="tabular-nums font-semibold text-brand-700">${links} <span class="font-normal text-slate-400">${esc(
            s.links_suffix,
          )}</span></td>
          <td class="tabular-nums font-medium text-slate-900">${price}</td>
          <td>
            <form id="${formId}" class="seal-add" data-variant-id="${p.variantId}">
              <input type="number" name="qty" min="1" step="1" value="${links}" class="input-sm w-16 text-right tabular-nums" aria-label="${esc(
                s.prod_amount,
              )}"/>
            </form>
          </td>
          <td class="text-right">
            <button type="submit" form="${formId}" class="btn-primary whitespace-nowrap px-3 py-1.5 text-xs">
              <i class="fa-solid fa-basket-shopping"></i>${esc(s.prod_add)}
            </button>
          </td>
        </tr>`;
      })
      .join("");
    return `<h2 class="mt-0">${esc(s.products_title)}</h2>
      <div class="card overflow-x-auto">
        <table class="table">
          <thead><tr>
            <th>${esc(s.prod_name)}</th>
            <th>${esc(s.prod_size)}</th>
            <th>${esc(s.prod_material)}</th>
            <th>${esc(s.prod_links)}</th>
            <th>${esc(s.prod_price)}</th>
            <th>${esc(s.prod_amount)}</th>
            <th></th>
          </tr></thead>
          <tbody>${body}</tbody>
        </table>
      </div>`;
  }

  // ---- SVG cross-section ----------------------------------------------------

  function buildSvg(pipe: number, bore: number, links: number, segColor: string): string {
    const S = 320;
    const c = S / 2;
    const boreR = 140;
    const pipeR = Math.max(6, Math.min(boreR - 4, (pipe / bore) * boreR));
    const gapPx = boreR - pipeR;
    const inset = Math.min(7, gapPx * 0.14);
    const rIn = pipeR + inset;
    const rOut = boreR - inset;

    const drawN = Math.min(links, 240);
    const pitch = (2 * Math.PI) / drawN;
    const spacing = pitch * 0.22;
    const start = -Math.PI / 2;
    let segs = "";
    let bolts = "";
    const showBolts = drawN <= 80 && rOut - rIn > 6;
    for (let i = 0; i < drawN; i++) {
      const a0 = start + i * pitch + spacing / 2;
      const a1 = start + (i + 1) * pitch - spacing / 2;
      segs += `<path d="${ringSegPath(c, rIn, rOut, a0, a1)}" fill="${segColor}" fill-opacity="0.9"/>`;
      if (showBolts) {
        const am = (a0 + a1) / 2;
        const rm = (rIn + rOut) / 2;
        bolts += `<circle cx="${f(c + rm * Math.cos(am))}" cy="${f(c + rm * Math.sin(am))}" r="${f(
          Math.max(1.4, (rOut - rIn) * 0.13),
        )}" fill="#fff" fill-opacity="0.85"/>`;
      }
    }

    return `<svg viewBox="0 0 ${S} ${S}" width="100%" style="max-width:340px" role="img">
      <defs>
        <pattern id="seal-hatch" width="8" height="8" patternTransform="rotate(45)" patternUnits="userSpaceOnUse">
          <rect width="8" height="8" fill="#eef2f6"/>
          <line x1="0" y1="0" x2="0" y2="8" stroke="#cbd5e1" stroke-width="1.1"/>
        </pattern>
      </defs>
      <rect x="0" y="0" width="${S}" height="${S}" rx="14" fill="url(#seal-hatch)"/>
      <circle cx="${c}" cy="${c}" r="${boreR}" fill="#f8fafc" stroke="#94a3b8" stroke-width="1.5" stroke-dasharray="5 4"/>
      <g>${segs}</g>
      <g>${bolts}</g>
      <circle cx="${c}" cy="${c}" r="${f(pipeR)}" fill="#475569" stroke="#334155" stroke-width="1.5"/>
      <circle cx="${c}" cy="${c}" r="${f(Math.max(3, pipeR * 0.7))}" fill="none" stroke="#64748b" stroke-width="1"/>
    </svg>`;
  }

  function placeholderSvg(): string {
    const S = 320, c = S / 2;
    return `<svg viewBox="0 0 ${S} ${S}" width="100%" style="max-width:340px" role="img">
      <rect x="0" y="0" width="${S}" height="${S}" rx="14" fill="#f1f5f9"/>
      <circle cx="${c}" cy="${c}" r="120" fill="none" stroke="#cbd5e1" stroke-width="1.5" stroke-dasharray="5 4"/>
    </svg>`;
  }

  // ---- readout / legend -----------------------------------------------------

  function renderReadout(
    pipe: number, bore: number, gap: number, links: number, active: SealChain | undefined, segColor: string,
  ): string {
    const sizeText = active
      ? `${esc(s.size_prefix)} ${esc(active.size)} · ${esc(materialLabel.get(active.material) ?? active.material)}`
      : "—";

    const rows: [string, string, string][] = [
      ["#f8fafc", esc(s.legend_bore), mm(bore)],
      ["#475569", esc(s.legend_pipe), mm(pipe)],
      [segColor, esc(s.gap_label), mm(gap)],
      [segColor, esc(s.legend_links), `<b class="text-slate-900">${links}</b> ${esc(s.links_suffix)}`],
    ];

    const items = rows
      .map(
        ([color, label, value]) => `
        <div class="flex items-center gap-2 text-sm">
          <span class="inline-block h-3 w-3 shrink-0 rounded-sm border border-slate-300" style="background:${color}"></span>
          <span class="text-slate-500">${label}</span>
          <span class="ml-auto tabular-nums text-slate-800">${value}</span>
        </div>`,
      )
      .join("");

    const rec = active
      ? `<div class="mt-2 border-t border-slate-100 pt-2 text-xs text-slate-500">${esc(
          s.recommended,
        )}: <span class="font-medium text-slate-700">${sizeText}</span><span class="text-slate-400"> · ${esc(
          s.col_pipe,
        )} ${rangeMm(active.adRohrMin, active.adRohrMax)}</span></div>`
      : "";

    return `<div class="grid grid-cols-1 gap-1.5 sm:grid-cols-2">${items}</div>${rec}`;
  }

  function nearest(pipe: number): SealChain | undefined {
    return sortByNearest(data.chains, pipe)[0];
  }

  function sortByNearest(pool: SealChain[], pipe: number): SealChain[] {
    return [...pool].sort(
      (a, b) =>
        Math.abs((a.adRohrMin + a.adRohrMax) / 2 - pipe) - Math.abs((b.adRohrMin + b.adRohrMax) / 2 - pipe),
    );
  }

  function orderBlock(): string {
    const { phone, email } = data.contact;
    const tel = phone.replace(/[^+\d]/g, "");
    return `<div class="mt-6 flex flex-col items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-5 text-center">
      <p class="text-sm text-slate-600">${esc(s.order_intro)}</p>
      <div class="flex flex-wrap justify-center gap-3">
        <a href="tel:${esc(tel)}" class="btn-primary"><i class="fa-solid fa-phone"></i>${esc(s.order_call)}</a>
        <a href="mailto:${esc(email)}" class="btn"><i class="fa-solid fa-envelope"></i>${esc(s.order_mail)}</a>
      </div>
    </div>`;
  }
}

// ---- geometry / helpers -----------------------------------------------------

function ringSegPath(c: number, rIn: number, rOut: number, a0: number, a1: number): string {
  const p = (r: number, a: number): [number, number] => [c + r * Math.cos(a), c + r * Math.sin(a)];
  const [x0o, y0o] = p(rOut, a0);
  const [x1o, y1o] = p(rOut, a1);
  const [x1i, y1i] = p(rIn, a1);
  const [x0i, y0i] = p(rIn, a0);
  return `M${f(x0o)} ${f(y0o)} A${f(rOut)} ${f(rOut)} 0 0 1 ${f(x1o)} ${f(y1o)} L${f(x1i)} ${f(y1i)} A${f(
    rIn,
  )} ${f(rIn)} 0 0 0 ${f(x0i)} ${f(y0i)} Z`;
}

function f(n: number): number {
  return Math.round(n * 100) / 100;
}

// Bump the nav cart-count badge (mirrors the cart island's bumpCount).
function bumpCount(by: number) {
  const link = document.querySelector<HTMLAnchorElement>("[data-cart-count]");
  if (!link) return;
  const badge = link.querySelector<HTMLElement>("[data-cart-badge]");
  if (!badge) return;
  const current = Number(link.dataset.count ?? badge.textContent ?? 0);
  const next = Math.max(0, current + by);
  link.dataset.count = String(next);
  badge.textContent = String(next);
  badge.classList.remove("bg-slate-100", "text-slate-500");
  badge.classList.add("bg-brand-600", "text-white");
  badge.classList.remove("animate-cart-pop");
  void badge.offsetWidth;
  badge.classList.add("animate-cart-pop");
}

// Briefly show an "added" confirmation on the basket button.
function flashAdded(btn: HTMLButtonElement, label: string) {
  const original = btn.innerHTML;
  btn.innerHTML = `<i class="fa-solid fa-check"></i>${esc(label)}`;
  btn.classList.add("!bg-emerald-600");
  window.setTimeout(() => {
    btn.innerHTML = original;
    btn.classList.remove("!bg-emerald-600");
    btn.disabled = false;
  }, 1400);
}

function parseData(elx: HTMLElement): SealCalcData {
  const empty: SealCalcData = {
    chains: [], products: [], materials: [], contact: { phone: "", email: "" }, lang: "en", strings: {},
  };
  try {
    return { ...empty, ...(JSON.parse(elx.textContent || "{}") as Partial<SealCalcData>) } as SealCalcData;
  } catch {
    return empty;
  }
}

function esc(s: string | undefined): string {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
